/**
 * Writing prompt panel component.
 *
 * Displays the writing prompt, optional image, and a scratchpad for notes in
 * a vertical stack so both stay visible. The split between prompt and notes
 * is resizable via a drag handle.
 *
 * Structurally aligned with Reading/Listening: the header uses the shared
 * `SimulationPanelHeader` (with an optional `TextSizeToolbar` when the prompt
 * is long), and the lower notes pane delegates to `NotesPanel variant="compact"`
 * so the scratchpad uses the same primitive as the Listening tab.
 *
 * @module WritingPromptPanel
 */

import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Maximize2, X } from "lucide-react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { Button } from "@/components/ui/button";
import {
  NotesPanel,
  SimulationPanelHeader,
  TextSizeToolbar,
  type TextSize,
} from "@/components/exam/shared";
import { cn } from "@/lib/utils";

interface WritingPromptPanelProps {
  readonly prompt: string;
  readonly imageUrl?: string;
  readonly taskType: string;
  readonly notes: string;
  readonly onNotesChange: (notes: string) => void;
  readonly navigationSlot?: ReactNode;
}

/**
 * Minimum prompt length (in characters) at which we expose an inline text-size
 * toolbar in the panel header. Shorter prompts don't need zoom controls.
 */
const TEXT_SIZE_THRESHOLD_CHARS = 300;

const textSizeClass: Record<TextSize, string> = {
  small: "text-[13px] sm:text-sm",
  medium: "text-sm sm:text-[15px]",
  large: "text-base sm:text-lg",
};

export function WritingPromptPanel({
  prompt,
  imageUrl,
  taskType,
  notes,
  onNotesChange,
  navigationSlot,
}: WritingPromptPanelProps) {
  const { t } = useTranslation();
  const [imageExpanded, setImageExpanded] = useState(false);
  const [textSize, setTextSize] = useState<TextSize>("medium");

  const panelTip = t("simulation.writing.prompt.panelTip", {
    defaultValue:
      "Drag the handle between Prompt and Notes to adjust space. Write your answer in the panel on the right.",
  });
  const notesPlaceholder = t("simulation.writing.prompt.notesPlaceholder", {
    defaultValue: "Jot down your ideas, outline, or key points here...",
  });
  const resizeAriaLabel = t("simulation.writing.prompt.resizeAriaLabel", {
    defaultValue: "Resize prompt and notes",
  });
  const imageExpandLabel = t("simulation.writing.prompt.imageExpand", {
    defaultValue: "Expand image preview",
  });
  const imageCloseLabel = t("simulation.writing.prompt.imageClose", {
    defaultValue: "Close image preview",
  });
  const imageAlt = t("simulation.writing.prompt.imageAlt", {
    defaultValue: "Task reference",
  });

  const shouldShowTextSize = prompt.length > TEXT_SIZE_THRESHOLD_CHARS;

  return (
    <div className="h-full flex flex-col min-h-0 bg-card border-b lg:border-r lg:border-b-0 border-border">
      <SimulationPanelHeader
        title={taskType}
        wrapTitle
        rightSlot={
          shouldShowTextSize ? (
            <TextSizeToolbar
              textSize={textSize}
              onTextSizeChange={setTextSize}
            />
          ) : null
        }
      />

      {/* Prompt + Notes: resizable vertical split */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        <PanelGroup direction="vertical" className="flex h-full min-h-0 flex-1">
          <Panel defaultSize={62} minSize={28} className="min-h-0">
            <div className="flex h-full min-h-0 flex-col overflow-hidden">
              <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin px-4 sm:px-5 py-4">
                <div className="space-y-4 sm:space-y-5">
                  {imageUrl && (
                    <div className="relative group">
                      <button
                        type="button"
                        className="w-full text-left p-0 border-none bg-transparent rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        onClick={() => setImageExpanded(!imageExpanded)}
                        aria-label={
                          imageExpanded ? imageCloseLabel : imageExpandLabel
                        }
                      >
                        <img
                          src={imageUrl}
                          alt={imageAlt}
                          className={cn(
                            "w-full rounded-lg border border-border transition-all cursor-pointer",
                            imageExpanded &&
                              "fixed inset-2 z-50 h-auto w-auto max-h-[calc(100dvh-1rem)] max-w-[calc(100vw-1rem)] object-contain bg-background/95 backdrop-blur-sm sm:inset-4 sm:max-h-[calc(100dvh-2rem)] sm:max-w-[calc(100vw-2rem)]",
                          )}
                        />
                      </button>
                      <Button
                        size="sm"
                        variant="secondary"
                        className={cn(
                          "absolute top-2 right-2 h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity",
                          imageExpanded &&
                            "fixed top-4 right-4 z-[60] opacity-100",
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          setImageExpanded(!imageExpanded);
                        }}
                      >
                        {imageExpanded ? (
                          <X className="w-3.5 h-3.5" />
                        ) : (
                          <Maximize2 className="w-3.5 h-3.5" />
                        )}
                      </Button>
                    </div>
                  )}
                  <div className="prose prose-sm max-w-none">
                    <p
                      className={cn(
                        "font-reading text-foreground leading-relaxed whitespace-pre-line",
                        textSizeClass[textSize],
                      )}
                    >
                      {prompt}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Panel>

          <PanelResizeHandle
            className={cn(
              "group relative flex h-2.5 w-full shrink-0 items-center justify-center",
              "border-y border-border bg-muted/40 outline-none transition-colors",
              "hover:bg-muted/70 focus-visible:bg-muted/70 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
              "cursor-row-resize",
            )}
            aria-orientation="horizontal"
            aria-label={resizeAriaLabel}
          >
            <span className="pointer-events-none rounded-full bg-border/80 px-2 py-0.5 opacity-70 group-hover:opacity-100 inline-flex items-center">
              <span
                aria-hidden
                className="block w-4 h-0.5 rounded-full bg-muted-foreground/60"
              />
            </span>
          </PanelResizeHandle>

          <Panel defaultSize={38} minSize={18} className="min-h-0">
            <NotesPanel
              variant="compact"
              notes={notes}
              onNotesChange={onNotesChange}
              title={t("simulation.writing.prompt.notesLabel", {
                defaultValue: "Notes",
              })}
              placeholder={notesPlaceholder}
              showClear={false}
            />
          </Panel>
        </PanelGroup>
      </div>

      {/* Instructions Footer */}
      <div className="px-4 py-3 border-t border-border bg-muted/30 shrink-0">
        <p className="font-ui text-[10px] sm:text-xs text-muted-foreground leading-tight">
          {panelTip}
        </p>
      </div>

      {navigationSlot}
    </div>
  );
}
